import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { CityCrossSell } from '@/components/detail/city-cross-sell';
import { CityHotels } from '@/components/detail/hotels-nearby';
import { ListingLinkPills } from '@/components/detail/listing-link-pills';
import { RelatedList } from '@/components/detail/related-list';
import { ArticleCard } from '@/components/riviera/article-card';
import { Fonts, Spacing } from '@/constants/theme';
import { useArticles } from '@todo/core/hooks/use-articles';
import { useFooterCityLinks } from '@/hooks/use-city-listing-pages';
import { useTheme } from '@/hooks/use-theme';
import { articleListingLinks, relatedArticles } from '@/lib/article-related';
import { articleCities } from '@/lib/i18n/article-content';
import type { Locale } from '@todo/core/i18n/locales';
import type { ArticleDetail } from '@todo/core/sanity/queries';

/**
 * Below an article's body: links to the listing pages its topics and cities
 * point at ("Plan your trip"), then sibling articles ("More articles"). Without
 * it an article ends in the footer — its only internal links are whatever the
 * author typed into the body, so it passes nothing on to the pages that earn
 * (boat rental, wine tours, events) and readers have nowhere to go next.
 * Between the two, tours and boats in the article's (first) city
 * (CityCrossSell): articles are among the few pages that get search clicks,
 * so whatever the topic they hand readers on to the affiliate catalogues.
 * Then hotels in that city (CityHotels).
 *
 * The article list comes from the locale's articles seed (useArticles), which
 * the page bakes into its head (ArticleHead's `relatedSeedKey`), so the cards
 * are in the static HTML and hydrate without a fetch.
 */
export function ArticleRelated({ article, locale }: { article: ArticleDetail; locale: Locale }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const pages = useFooterCityLinks();
  const { articles } = useArticles(locale);

  const links = articleListingLinks(article, pages, locale, t);
  const related = relatedArticles(article, articles);
  const crossSellCityId = articleCities(article.cities)[0]?.id ?? null;

  return (
    <>
      {links.length > 0 ? (
        <View style={styles.centered}>
          <View style={styles.page}>
            <Text
              role="heading"
              aria-level={2}
              style={[styles.heading, { color: theme.text, fontFamily: Fonts?.serif }]}>
              {t('articles.planHeading')}
            </Text>
            <ListingLinkPills links={links} />
          </View>
        </View>
      ) : null}

      <CityCrossSell
        cityId={crossSellCityId}
        // "Plan your trip" may already link the same pages (a boats article).
        omitLinkKeys={links.map((link) => link.key)}
      />

      {crossSellCityId ? (
        <View style={styles.centered}>
          <View style={styles.page}>
            <CityHotels cityId={crossSellCityId} campaign="article" pageId={article._id} />
          </View>
        </View>
      ) : null}

      {related.length > 0 ? (
        <RelatedList
          heading={t('articles.moreHeading')}
          seeAllHref={{ pathname: '/[lang]/articles', params: { lang: locale } }}>
          {related.map((a) => (
            <ArticleCard key={a._id} article={a} locale={locale} />
          ))}
        </RelatedList>
      ) : null}
    </>
  );
}

// Same column as RelatedList (four 240px cards across) so the two sections
// line up.
const GRID_MAX_WIDTH = 240 * 4 + Spacing.three * 3;

const styles = StyleSheet.create({
  centered: {
    alignItems: 'center',
    width: '100%',
    marginTop: Spacing.five,
    paddingHorizontal: Spacing.three,
  },
  page: {
    width: '100%',
    maxWidth: GRID_MAX_WIDTH,
    gap: Spacing.three,
  },
  heading: {
    fontSize: 22,
    fontWeight: '700',
  },
});
